import type { MapTile, TileType, WorldEntity } from '../../engine/world/types';
import { createBuildingMetadata } from '../buildings/BuildingCatalog';
import { BiomeGenerator } from './BiomeGenerator';
import { LakeGenerator } from './LakeGenerator';
import { RiverGenerator } from './RiverGenerator';
import { SeededRandom } from './SeededRandom';
import { TerrainGenerator } from './TerrainGenerator';
import { WaterTilePlanner, type WaterTilePlan } from './WaterTilePlanner';
import { WorldDiagnosticsBuilder } from './WorldDiagnostics';
import { createWorldGeneratorConfig, type CreateWorldGeneratorConfigInput } from './WorldGeneratorConfig';
import type { GeneratedWorld, PlannedBuilding, PlannedDecoration, Point, RoadGraph, SettlementGraph, SettlementNodeKind, WorldGeneratorConfig } from './types';

interface TileSeed {
  type: TileType;
  variant?: string;
}

export class WorldGenerator {
  private readonly config: WorldGeneratorConfig;
  private readonly random: SeededRandom;
  private readonly diagnostics = new WorldDiagnosticsBuilder();

  constructor(input: CreateWorldGeneratorConfigInput = {}) {
    this.config = createWorldGeneratorConfig(input);
    this.random = new SeededRandom(this.config.seed.seed);
  }

  generate(): GeneratedWorld {
    const grid = this.createBaseGrid();
    const bridgePoints = new Set<string>();
    const plannedBuildings = createDynamicPlannedBuildings(this.config);
    const roadPaths = createDynamicRoadPaths(this.config, plannedBuildings);
    const heightMap = new TerrainGenerator(this.config).generateHeightMap();
    const river = new RiverGenerator(this.config).generateRiver(heightMap);
    const lakeGenerator = new LakeGenerator(this.config);
    const lake = lakeGenerator.generateLake();
    const riverMouthLake = lakeGenerator.generateRiverMouthLake(river);
    const waterFeatures = riverMouthLake ? [river, lake, riverMouthLake] : [river, lake];
    const waterPlan = new WaterTilePlanner(this.config).plan(waterFeatures);

    this.placeDynamicMountains(grid);
    this.placeWaterFeatures(grid, bridgePoints, waterPlan, plannedBuildings, roadPaths);
    this.placeDynamicForests(grid);
    this.placeDynamicFields(grid, plannedBuildings, roadPaths);
    this.placeDynamicRoads(grid, bridgePoints, roadPaths);

    this.diagnostics.addNote('WorldGenerator 3.0 Shell aktiv: MapLayout nutzt jetzt den neuen Orchestrator.');
    this.diagnostics.addNote('TerrainGenerator und BiomeGenerator aktiv: Heightmap/BiomeMap sind serialisiert, Rendering bleibt getrennt.');
    this.diagnostics.addNote(`RiverGenerator aktiv: ${river.source.x}:${river.source.y} -> ${river.mouth.x}:${river.mouth.y}, ${river.path.length} Wegpunkte.`);
    this.diagnostics.addNote(`LakeGenerator aktiv: ${lake.tiles.length} Wasserfelder, ${lake.shoreline.length} Uferfelder.`);
    for (const note of waterPlan.notes) this.diagnostics.addNote(note);
    for (const invalidWater of waterPlan.invalidWater) this.diagnostics.addInvalidWater(invalidWater);
    this.diagnostics.addNote('Sichtbare Wasserplatzierung nutzt Planner-Ausgabe mit Gebäude- und Brückenschutz.');
    this.diagnostics.addNote('Rendering erzeugt weiterhin keine Weltlogik.');

    const tiles = this.toTiles(grid);
    const biomeMap = new BiomeGenerator(this.config).generateBiomeMap(heightMap, tiles);
    const plannedDecorations = createDynamicPlannedDecorations(this.config, bridgePoints, plannedBuildings, tiles);
    return {
      map: { width: this.config.width, height: this.config.height, tiles },
      rawTiles: tiles,
      heightMap,
      biomeMap,
      waterFeatures,
      settlementGraph: createDynamicSettlementGraph(plannedBuildings),
      districts: [],
      roadGraph: createDynamicRoadGraph(plannedBuildings, roadPaths),
      plots: [],
      plannedBuildings,
      plannedDecorations,
      buildings: plannedBuildings.map(toWorldBuilding),
      decorations: plannedDecorations.map(toWorldDecoration),
      diagnostics: this.diagnostics.toJSON(),
    };
  }

  private createBaseGrid(): TileSeed[][] {
    return Array.from({ length: this.config.height }, (_, y) =>
      Array.from({ length: this.config.width }, (_, x) => ({
        type: 'grass' as const,
        variant: (x * 11 + y * 7 + this.random.int(0, 1)) % 13 === 0 ? 'dark' : 'light',
      })),
    );
  }

  private placeDynamicMountains(grid: TileSeed[][]): void {
    this.placeSeededClusters(grid, 'mountain', 2, 5);
  }

  private placeWaterFeatures(
    grid: TileSeed[][],
    bridgePoints: Set<string>,
    waterPlan: WaterTilePlan,
    buildings: readonly PlannedBuilding[],
    roadPaths: readonly Point[][],
  ): void {
    const protectedCells = protectedBuildingCells(buildings);
    let waterTiles = 0;

    for (const write of waterPlan.writes) {
      if (write.type !== 'water') continue;
      if (protectedCells.has(key(write))) {
        this.diagnostics.addBlockedPlot(`Wasser bei ${write.x}:${write.y} wurde blockiert, weil dort ein Gebäude geplant ist.`);
        continue;
      }
      this.set(grid, write.x, write.y, 'water', write.variant);
      waterTiles++;
    }

    if (waterTiles < 10) {
      this.diagnostics.addInvalidWater('Die dynamische Wasserplanung hat zu wenige sichtbare Felder erzeugt.');
    }

    this.placeRoadBridges(grid, bridgePoints, roadPaths);
    if (waterPlan.bridgeCandidates.length === 0) this.diagnostics.addInvalidWater('Keine möglichen Brückenfelder im Wasserplan gefunden.');
  }

  private placeRoadBridges(grid: TileSeed[][], bridgePoints: Set<string>, roadPaths: readonly Point[][]): void {
    for (const roadPath of roadPaths) {
      for (let index = 0; index < roadPath.length; index++) {
        const point = roadPath[index];
        if (grid[point.y]?.[point.x]?.type !== 'water') continue;
        if (point.x < 20) continue;
        const crossingStart = index;
        while (index + 1 < roadPath.length && grid[roadPath[index + 1].y]?.[roadPath[index + 1].x]?.type === 'water') index++;
        const crossingEnd = index;
        const crossingPoints = roadPath.slice(crossingStart, crossingEnd + 1);
        const bridgeIndex = Math.floor((crossingStart + crossingEnd) / 2);
        const bridgePoint = roadPath[bridgeIndex];
        const previous = roadPath[crossingStart - 1];
        const next = roadPath[crossingEnd + 1];
        const horizontal = (previous?.y === bridgePoint.y && previous.x !== bridgePoint.x) || (next?.y === bridgePoint.y && next.x !== bridgePoint.x);
        const variant = horizontal ? 'bridge_h' : 'bridge_v';
        for (const crossingPoint of crossingPoints) {
          this.set(grid, crossingPoint.x, crossingPoint.y, 'water', variant);
          bridgePoints.add(key(crossingPoint));
        }
      }
    }
  }

  private placeDynamicForests(grid: TileSeed[][]): void {
    this.placeSeededClusters(grid, 'forest', 4, 6);
  }

  private placeDynamicFields(grid: TileSeed[][], buildings: readonly PlannedBuilding[], roadPaths: readonly Point[][]): void {
    if (this.config.targetPhaseIndex < 2) return;
    this.placeFieldPatch(grid, buildings, roadPaths, this.random.shuffle([
      { x: 10, y: 10 }, { x: 5, y: 8 }, { x: 5, y: 10 }, { x: 11, y: 13 }, { x: 14, y: 16 },
    ]));
    if (this.config.targetPhaseIndex < 3) return;
    this.placeFieldPatch(grid, buildings, roadPaths, this.random.shuffle([
      { x: 19, y: 15 }, { x: 22, y: 8 }, { x: 23, y: 14 }, { x: 9, y: 16 }, { x: 18, y: 2 },
    ]));
  }

  private placeFieldPatch(
    grid: TileSeed[][],
    buildings: readonly PlannedBuilding[],
    roadPaths: readonly Point[][],
    candidates: readonly Point[],
  ): void {
    const protectedCells = protectedBuildingCells(buildings);
    const roadCells = new Set(roadPaths.flat().map(key));
    const origin = candidates.find((candidate) => {
      for (let y = candidate.y; y < candidate.y + 2; y++) {
        for (let x = candidate.x; x < candidate.x + 4; x++) {
          const tile = grid[y]?.[x];
          const cellKey = `${x}:${y}`;
          if (!tile || tile.type !== 'grass' || protectedCells.has(cellKey) || roadCells.has(cellKey)) return false;
        }
      }
      return true;
    });
    if (!origin) {
      this.diagnostics.addBlockedPlot('Kein kollisionsfreier Platz für ein Getreidefeld gefunden.');
      return;
    }
    for (let y = origin.y; y < origin.y + 2; y++) {
      for (let x = origin.x; x < origin.x + 4; x++) this.set(grid, x, y, 'field', 'wheat');
    }
  }

  private placeDynamicRoads(grid: TileSeed[][], bridgePoints: Set<string>, roadPaths: readonly Point[][]): void {
    for (const roadPath of roadPaths) {
      for (const point of roadPath) {
        if (bridgePoints.has(key(point))) continue;
        if (grid[point.y]?.[point.x]?.type !== 'water') this.set(grid, point.x, point.y, 'road', 'auto');
      }
    }
  }

  private placeCluster(grid: TileSeed[][], type: TileType, points: readonly Point[]): void {
    for (const point of points) {
      if (grid[point.y]?.[point.x]?.type === 'water' || grid[point.y]?.[point.x]?.type === 'road') continue;
      this.set(grid, point.x, point.y, type);
    }
  }

  private placeSeededClusters(grid: TileSeed[][], type: TileType, clusterCount: number, tilesPerCluster: number): void {
    for (let cluster = 0; cluster < clusterCount; cluster++) {
      const center = {
        x: this.random.int(2, Math.max(2, this.config.width - 3)),
        y: this.random.int(2, Math.max(2, this.config.height - 3)),
      };
      const points = Array.from({ length: tilesPerCluster }, () => ({
        x: clampNumber(1, this.config.width - 2, center.x + this.random.int(-2, 2)),
        y: clampNumber(1, this.config.height - 2, center.y + this.random.int(-2, 2)),
      }));
      this.placeCluster(grid, type, points);
    }
  }

  private set(grid: TileSeed[][], x: number, y: number, type: TileType, variant?: string): void {
    if (!grid[y]?.[x]) return;
    grid[y][x] = { type, variant };
  }

  private toTiles(grid: TileSeed[][]): readonly MapTile[] {
    return grid.flatMap((row, y) => row.map((tile, x) => ({ x, y, ...tile })));
  }
}

export function generateWorld(input: CreateWorldGeneratorConfigInput = {}): GeneratedWorld {
  return new WorldGenerator(input).generate();
}

function createDynamicSettlementGraph(buildings: readonly PlannedBuilding[]): SettlementGraph {
  const center = buildings.find((building) => building.kind === 'castle') ?? buildings[0];
  return {
    nodes: buildings.map((building) => ({
      id: building.id,
      kind: settlementKindForBuilding(building.kind),
      x: building.position.x,
      y: building.position.y,
      radius: building.kind === 'castle' ? 4 : 2,
      priority: building.kind === 'castle' ? 100 : 70,
    })),
    edges: center
      ? buildings
        .filter((building) => building.id !== center.id)
        .map((building) => ({ from: center.id, to: building.id, priority: building.kind === 'market' ? 90 : 70 }))
      : [],
  };
}

function settlementKindForBuilding(kind: string): SettlementNodeKind {
  if (kind === 'castle') return 'castle';
  if (kind === 'market' || kind === 'warehouse') return 'market';
  if (kind === 'farm' || kind === 'mill' || kind === 'bakery') return 'farm';
  if (kind === 'mine') return 'mine';
  if (kind === 'watchtower') return 'military';
  if (kind === 'woodcutter_camp') return 'forest';
  if (kind === 'smithy') return 'craft';
  return 'housing';
}

function createDynamicRoadPaths(
  config: WorldGeneratorConfig,
  buildings: readonly PlannedBuilding[],
): readonly Point[][] {
  const castle = buildings.find((building) => building.kind === 'castle') ?? buildings[0];
  if (!castle) return [];
  const hub = buildingRoadAnchor(castle);
  return buildings
    .filter((building) => building.id !== castle.id)
    .map((building, index) => {
      const door = buildingRoadAnchor(building);
      const horizontalFirst = seededIndex(config, `road:${building.id}:${index}`) === 0;
      const bend = horizontalFirst
        ? { x: door.x, y: hub.y }
        : { x: hub.x, y: door.y };
      return path(hub, bend, door);
    });
}

function buildingRoadAnchor(building: PlannedBuilding): Point {
  const doorOffset = building.kind === 'castle' ? 1 : 0.6;
  return {
    x: Math.round(building.position.x),
    y: Math.round(building.position.y + doorOffset),
  };
}

function createDynamicRoadGraph(buildings: readonly PlannedBuilding[], roadPaths: readonly Point[][]): RoadGraph {
  return {
    nodes: buildings.map((building) => {
      const door = buildingRoadAnchor(building);
      return { id: building.id, x: door.x, y: door.y, kind: building.kind === 'castle' ? 'main' : 'side' };
    }),
    edges: roadPaths.map((roadPath, index) => ({
      from: buildings[0]?.id ?? `road-${index}-start`,
      to: buildings[index + 1]?.id ?? `road-${index}-end`,
      kind: 'main',
      path: roadPath,
    })),
  };
}

function createDynamicPlannedBuildings(config: WorldGeneratorConfig): readonly PlannedBuilding[] {
  const all = [
    plannedBuilding('hq', 'castle', 15.9, 8.15, 'Burg', stageFor(config, 'hq', 2), 'stone-blue-roof'),
    plannedBuilding('warehouse', 'warehouse', 16.05, 10.2, 'Lager', stageFor(config, 'warehouse', 1), 'blue-roof-store'),
    plannedBuilding('market', 'market', 12.15, 7.85, 'Markt', stageFor(config, 'market', 2), 'square-blue'),
    plannedBuilding('house-1', 'house', 10.85, 7.65, 'Haus', stageFor(config, 'house-1', 1), 'timber-a'),
    plannedBuilding('house-3', 'house', 17.25, 7.8, 'Haus', stageFor(config, 'house-3', 3), 'timber-c'),
    plannedBuilding('house-2', 'house', 19.15, 9.35, 'Haus', stageFor(config, 'house-2', 3), 'timber-b'),
    plannedBuilding('woodcutter', 'woodcutter_camp', 6.2, 5.0, 'Holzfäller', stageFor(config, 'woodcutter', 1), 'forest-edge'),
    plannedBuilding('farm', 'farm', 9.7, 7.65, 'Farm', stageFor(config, 'farm', 1), 'grain-yard'),
    plannedBuilding('mill', 'mill', 19.85, 10.0, 'Mühle', stageFor(config, 'mill', 3), 'windmill'),
    plannedBuilding('bakery', 'bakery', 16.25, 14.35, 'Bäckerei', stageFor(config, 'bakery', 3), 'oven-yard'),
    plannedBuilding('smithy', 'smithy', 20.35, 14.45, 'Schmiede', stageFor(config, 'smithy', 4), 'forge-yard'),
    plannedBuilding('mine', 'mine', 21.9, 3.75, 'Mine', stageFor(config, 'mine', 2), 'mountain-shaft'),
    plannedBuilding('watchtower', 'watchtower', 28.55, 10.15, 'Wachturm', stageFor(config, 'watchtower', 4), 'frontier'),
  ];
  const visible = all
    .filter((building) => requiredGrowth(building.id) <= config.targetPhaseIndex)
    .map((building) => shiftBuilding(config, building));
  return resolveBuildingCollisions(config, visible);
}

function protectedBuildingCells(buildings: readonly PlannedBuilding[]): Set<string> {
  const cells = new Set<string>();
  for (const building of buildings) {
    const bounds = buildingBounds(building, 0.18);
    for (let y = Math.floor(bounds.top); y <= Math.ceil(bounds.bottom); y++) {
      for (let x = Math.floor(bounds.left); x <= Math.ceil(bounds.right); x++) {
        if (rectanglesOverlap(bounds, { left: x - 0.5, right: x + 0.5, top: y - 0.5, bottom: y + 0.5 })) {
          cells.add(`${x}:${y}`);
        }
      }
    }
  }
  return cells;
}

function createDynamicPlannedDecorations(
  config: WorldGeneratorConfig,
  bridgePoints: Set<string>,
  buildings: readonly PlannedBuilding[],
  tiles: readonly MapTile[],
): readonly PlannedDecoration[] {
  const decorations = [
    plannedDecoration('pine-01', 'tree_pine', 2.4, 2.7),
    plannedDecoration('pine-02', 'tree_pine', 3.5, 2.3),
    plannedDecoration('pine-03', 'tree_oak', 4.7, 3.2),
    plannedDecoration('pine-04', 'tree_pine', 5.8, 4.8),
    plannedDecoration('forest-cluster-01', 'tree_pine', 2.2, 4.2),
    plannedDecoration('forest-cluster-02', 'tree_oak', 3.1, 4.7),
    plannedDecoration('forest-cluster-03', 'tree_pine', 4.0, 4.3),
    plannedDecoration('forest-cluster-04', 'tree_oak', 2.6, 5.4),
    plannedDecoration('forest-cluster-05', 'tree_pine', 3.7, 5.6),
    plannedDecoration('forest-cluster-06', 'tree_oak', 4.8, 5.2),
    plannedDecoration('forest-log-01', 'log', 5.4, 6.8),
    plannedDecoration('forest-stump-01', 'stump', 4.6, 6.5),
    plannedDecoration('forest-bush-01', 'bush', 6.2, 5.7),
    plannedDecoration('forest-mushrooms-01', 'mushrooms', 3.8, 5.4),
    plannedDecoration('oak-01', 'tree_oak', 22.3, 8.1),
    plannedDecoration('oak-02', 'tree_pine', 24.1, 8.4),
    plannedDecoration('oak-03', 'tree_oak', 10.3, 15.3),
    plannedDecoration('oak-04', 'tree_oak', 11.6, 15.8),
    plannedDecoration('pine-05', 'tree_pine', 23.4, 14.6),
    plannedDecoration('pine-06', 'tree_pine', 25.0, 15.3),
    plannedDecoration('rock-01', 'rocks', 25.6, 3.3),
    plannedDecoration('rock-02', 'rocks', 24.1, 5.2),
    plannedDecoration('rock-03', 'rocks', 28.4, 4.8),
    plannedDecoration('rock-04', 'rocks', 21.6, 3.1),
    plannedDecoration('bush-01', 'bush', 15.5, 8.6),
    plannedDecoration('bush-02', 'bush', 19.5, 13.1),
    plannedDecoration('bush-03', 'bush', 7.7, 12.5),
    plannedDecoration('flowers-village-01', 'flowers_white', 16.8, 9.7),
    plannedDecoration('flowers-village-02', 'flowers_blue', 11.5, 10.4),
    plannedDecoration('flowers-field-01', 'flowers_white', 9.5, 8.1),
    plannedDecoration('flowers-field-02', 'flowers_blue', 20.5, 10.7),
    plannedDecoration('flowers-meadow-01', 'flowers_white', 4.0, 11.4),
    plannedDecoration('flowers-meadow-02', 'flowers_blue', 26.4, 9.4),
    plannedDecoration('market-crate-01', 'crate', 12.5, 9.8),
    plannedDecoration('market-barrel-01', 'barrel', 13.7, 9.8),
    plannedDecoration('warehouse-crate-01', 'crate', 14.1, 11.7),
    plannedDecoration('warehouse-barrel-01', 'barrel', 15.4, 11.8),
    plannedDecoration('bakery-barrel-01', 'barrel', 15.8, 13.6),
    plannedDecoration('bakery-crate-01', 'crate', 16.9, 13.6),
    plannedDecoration('smithy-crate-01', 'crate', 19.0, 13.9),
    plannedDecoration('smithy-barrel-01', 'barrel', 20.1, 13.8),
    plannedDecoration('village-fence-01', 'fence_h', 16.2, 7.0),
    plannedDecoration('village-fence-02', 'fence_h', 17.8, 7.0),
    plannedDecoration('farm-fence-01', 'fence_h', 10.9, 10.0),
    plannedDecoration('farm-fence-02', 'fence_h', 12.8, 10.0),
    plannedDecoration('farm-crate-01', 'crate', 10.0, 10.4),
    plannedDecoration('lake-rocks-01', 'rocks', 6.0, 12.0),
    plannedDecoration('lake-bush-01', 'bush', 7.2, 11.6),
    plannedDecoration('lake-pine-01', 'tree_pine', 5.8, 14.2),
    plannedDecoration('lake-oak-01', 'tree_oak', 9.8, 14.1),
    plannedDecoration('mill-barrel-01', 'barrel', 18.9, 11.1),
    plannedDecoration('mine-crate-01', 'crate', 24.6, 4.7),
    plannedDecoration('mine-barrel-01', 'barrel', 25.9, 4.8),
    plannedDecoration('house-fence-01', 'fence_h', 11.8, 8.8),
    plannedDecoration('house-flowers-01', 'flowers_white', 17.3, 11.5),
    plannedDecoration('castle-banner-01', 'banner_blue', 16.0, 8.2),
    plannedDecoration('watch-rocks-01', 'rocks', 28.5, 13.2),
    plannedDecoration('watch-bush-01', 'bush', 29.7, 11.4),
    plannedDecoration('riverbank-pine-01', 'tree_pine', 27.3, 15.7),
    plannedDecoration('riverbank-pine-02', 'tree_pine', 28.5, 15.0),
    plannedDecoration('riverbank-oak-01', 'tree_oak', 29.7, 15.8),
    plannedDecoration('riverbank-bush-01', 'bush', 27.9, 14.2),
    plannedDecoration('riverbank-rocks-01', 'rocks', 30.0, 13.9),
    plannedDecoration('riverbank-rocks-02', 'rocks', 29.1, 16.8),
    plannedDecoration('riverbank-pine-03', 'tree_pine', 26.2, 13.7),
    plannedDecoration('riverbank-oak-02', 'tree_oak', 28.7, 6.2),
    plannedDecoration('riverbank-bush-02', 'bush', 26.8, 7.6),
    plannedDecoration('riverbank-rocks-03', 'rocks', 27.8, 5.5),
    plannedDecoration('riverbank-rocks-04', 'rocks', 24.8, 15.2),
    plannedDecoration('meadow-pine-01', 'tree_pine', 31.0, 10.0),
    plannedDecoration('meadow-pine-02', 'tree_pine', 30.3, 15.1),
    plannedDecoration('meadow-oak-01', 'tree_oak', 28.2, 17.3),
    plannedDecoration('meadow-rocks-01', 'rocks', 31.4, 16.8),
    plannedDecoration('meadow-forest-01', 'tree_pine', 29.2, 17.5),
    plannedDecoration('meadow-forest-02', 'tree_oak', 30.2, 17.9),
    plannedDecoration('meadow-forest-03', 'tree_pine', 31.2, 17.5),
    plannedDecoration('meadow-forest-04', 'tree_oak', 29.7, 18.6),
    plannedDecoration('meadow-forest-05', 'tree_pine', 30.8, 18.5),
    plannedDecoration('meadow-forest-06', 'tree_oak', 31.8, 18.3),
    plannedDecoration('lake-oak-02', 'tree_oak', 4.3, 13.4),
    plannedDecoration('lake-pine-02', 'tree_pine', 7.4, 15.8),
    plannedDecoration('lake-rocks-02', 'rocks', 2.7, 12.9),
  ];
  const bridgeDecorations = [...bridgePoints].map((cell, index) => {
    const [x, y] = cell.split(':').map(Number);
    return plannedDecoration(`bridge-${index}`, 'bridge_h', x, y);
  });
  const visibleDecorations = decorations
    .filter((decoration) => requiredGrowth(decoration.id) <= config.targetPhaseIndex)
    .filter((_, index) => config.targetPhaseIndex >= 4 || index % 3 !== seededIndex(config, 'decor-thin'))
    .map((decoration) => shiftDecoration(config, decoration))
    .filter((decoration) => isDecorationPlacementClear(decoration, buildings, tiles));
  return [...removeDecorationCollisions(visibleDecorations), ...bridgeDecorations];
}

function plannedBuilding(id: string, kind: string, x: number, y: number, label: string, stage: 1 | 2 | 3 | 4, variantId: string): PlannedBuilding {
  return { id, kind, label, stage, variantId, plotId: `plot-${id}`, position: { x, y } };
}

function plannedDecoration(id: string, kind: string, x: number, y: number): PlannedDecoration {
  return { id, kind, position: { x, y } };
}

function toWorldBuilding(building: PlannedBuilding): WorldEntity {
  return {
    id: building.id,
    type: 'building',
    kind: building.kind,
    x: building.position.x,
    y: building.position.y,
    label: building.label,
    building: createBuildingMetadata(building.kind, building.stage, building.variantId),
  };
}

function toWorldDecoration(decoration: PlannedDecoration): WorldEntity {
  return { id: decoration.id, type: 'environment', kind: decoration.kind, x: decoration.position.x, y: decoration.position.y };
}

function stageFor(config: WorldGeneratorConfig, id: string, unlockLevel: number): 1 | 2 | 3 | 4 {
  const base = Math.max(1, Math.min(4, config.targetPhaseIndex - unlockLevel + 1));
  const bonus = seededIndex(config, `${id}:stage`) === 0 && config.targetPhaseIndex >= 4 ? 1 : 0;
  return Math.max(1, Math.min(4, base + bonus)) as 1 | 2 | 3 | 4;
}

function requiredGrowth(id: string): number {
  if (
    id === 'hq' ||
    id === 'warehouse' ||
    id === 'house-1' ||
    id.startsWith('lake-') ||
    /^pine-0[1-4]$/.test(id) ||
    id.startsWith('forest-cluster-') ||
    id.startsWith('forest-log-') ||
    id.startsWith('forest-stump-') ||
    id.startsWith('forest-bush-') ||
    id.startsWith('forest-mushrooms-') ||
    /^rock-0[1-4]$/.test(id) ||
    id.startsWith('warehouse-') ||
    id.startsWith('castle-banner-')
  ) return 1;
  if (id === 'market' || id === 'woodcutter' || id === 'farm' || id === 'mine' || id.startsWith('forest-') || id.startsWith('mine-')) return 2;
  if (id === 'house-2' || id === 'house-3' || id === 'mill' || id === 'bakery' || id.startsWith('farm-') || id.startsWith('house-')) return 3;
  if (id === 'smithy' || id === 'watchtower' || id.startsWith('watch-') || id.startsWith('riverbank-') || id.startsWith('meadow-')) return 4;
  return 2;
}

function shiftBuilding(config: WorldGeneratorConfig, building: PlannedBuilding): PlannedBuilding {
  const safeIds = new Set(['house-1', 'house-2', 'house-3', 'farm', 'mill', 'bakery', 'smithy']);
  const settlementOffset = seededJitter(config, 'settlement-origin', 1.25);
  const jitter = safeIds.has(building.id) ? seededJitter(config, building.id, 0.18) : { x: 0, y: 0 };
  return {
    ...building,
    position: {
      x: clampNumber(4, config.width - 4, building.position.x + settlementOffset.x + jitter.x),
      y: clampNumber(5.5, config.height - 2.5, building.position.y + settlementOffset.y + jitter.y),
    },
  };
}

function shiftDecoration(config: WorldGeneratorConfig, decoration: PlannedDecoration): PlannedDecoration {
  if (decoration.kind.startsWith('bridge')) return decoration;
  const jitter = seededJitter(config, decoration.id, 0.28);
  return {
    ...decoration,
    position: {
      x: clampNumber(0.6, config.width - 0.8, decoration.position.x + jitter.x),
      y: clampNumber(0.6, config.height - 0.8, decoration.position.y + jitter.y),
    },
  };
}

interface PlacementBounds {
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly bottom: number;
}

function resolveBuildingCollisions(
  config: WorldGeneratorConfig,
  buildings: readonly PlannedBuilding[],
): readonly PlannedBuilding[] {
  const placed: PlannedBuilding[] = [];

  for (const building of buildings) {
    const candidates = buildingCandidates(building.position);
    const position = candidates.find((candidate) => {
      const moved = { ...building, position: candidate };
      const bounds = buildingBounds(moved, 0.16);
      // Building sprites are anchored at their feet and visually reach much
      // farther upward than their logical footprint. Keep enough headroom so
      // roofs never get clipped by the top edge of the overlay.
      if (candidate.y < 4.2) return false;
      if (bounds.left < 0.6 || bounds.right > config.width - 0.6 || bounds.top < 0.6 || bounds.bottom > config.height - 0.6) {
        return false;
      }
      return placed.every((other) => !rectanglesOverlap(bounds, buildingBounds(other, 0.28)));
    });
    placed.push(position ? { ...building, position } : building);
  }

  return placed;
}

function buildingCandidates(origin: Point): readonly Point[] {
  const candidates: Point[] = [{ ...origin }];
  for (let radius = 0.5; radius <= 3; radius += 0.5) {
    for (let y = -radius; y <= radius; y += 0.5) {
      for (let x = -radius; x <= radius; x += 0.5) {
        if (Math.max(Math.abs(x), Math.abs(y)) !== radius) continue;
        candidates.push({ x: origin.x + x, y: origin.y + y });
      }
    }
  }
  return candidates.sort((a, b) =>
    Math.hypot(a.x - origin.x, a.y - origin.y) - Math.hypot(b.x - origin.x, b.y - origin.y),
  );
}

function buildingBounds(building: PlannedBuilding, margin = 0): PlacementBounds {
  const isCastle = building.kind === 'castle';
  const isTall = building.kind === 'watchtower' || building.kind === 'mill';
  const isWide = building.kind === 'farm' || building.kind === 'market';
  // Always reserve the complete future level-six castle plot. Later upgrades
  // therefore grow inside their original parcel instead of covering streets,
  // fields, decorations or buildings that appeared in earlier phases.
  const halfWidth = isCastle ? 3.45 : isWide ? 0.75 : isTall ? 0.58 : 0.64;
  const height = isCastle ? 6.85 : isTall ? 1.9 : 1.55;
  return {
    left: building.position.x - halfWidth - margin,
    right: building.position.x + halfWidth + margin,
    top: building.position.y - height - margin,
    bottom: building.position.y + ( isCastle ? 0.8 : 0.45 ) + margin,
  };
}

function decorationBounds(decoration: PlannedDecoration, margin = 0): PlacementBounds {
  const isTree = decoration.kind === 'tree_oak' || decoration.kind === 'tree_pine';
  const isLargeGroundProp = decoration.kind === 'rocks' || decoration.kind === 'bush' || decoration.kind.startsWith('fence');
  const halfWidth = isTree ? 0.86 : decoration.kind === 'rocks' ? 0.68 : isLargeGroundProp ? 0.58 : 0.42;
  const height = isTree ? 1.6 : decoration.kind === 'rocks' ? 0.9 : isLargeGroundProp ? 0.78 : 0.55;
  return {
    left: decoration.position.x - halfWidth - margin,
    right: decoration.position.x + halfWidth + margin,
    top: decoration.position.y - height - margin,
    bottom: decoration.position.y + 0.25 + margin,
  };
}

function isDecorationPlacementClear(
  decoration: PlannedDecoration,
  buildings: readonly PlannedBuilding[],
  tiles: readonly MapTile[],
): boolean {
  const bounds = decorationBounds(decoration, 0.12);
  if (buildings.some((building) => rectanglesOverlap(bounds, buildingBounds(building, 0.18)))) return false;

  return tiles.every((tile) => {
    if (tile.type === 'grass' || tile.type === 'forest') return true;
    return !rectanglesOverlap(bounds, {
      left: tile.x - 0.48,
      right: tile.x + 0.48,
      top: tile.y - 0.48,
      bottom: tile.y + 0.48,
    });
  });
}

function removeDecorationCollisions(decorations: readonly PlannedDecoration[]): readonly PlannedDecoration[] {
  const accepted: PlannedDecoration[] = [];
  for (const decoration of decorations) {
    const bounds = decorationBounds(decoration, 0.12);
    if (accepted.some((other) => {
      const bothTrees = isTreeDecoration(decoration) && isTreeDecoration(other);
      if (bothTrees) {
        // In Waldgruppen dürfen sich nur die Kronen überlappen. Die Stämme
        // behalten genug Abstand, damit einzelne Bäume lesbar bleiben.
        return Math.hypot(
          decoration.position.x - other.position.x,
          decoration.position.y - other.position.y,
        ) < 0.72;
      }
      return rectanglesOverlap(bounds, decorationBounds(other, 0.12));
    })) continue;
    accepted.push(decoration);
  }
  return accepted;
}

function isTreeDecoration(decoration: PlannedDecoration): boolean {
  return decoration.kind === 'tree_oak' || decoration.kind === 'tree_pine';
}

function rectanglesOverlap(a: PlacementBounds, b: PlacementBounds): boolean {
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

function seededJitter(config: WorldGeneratorConfig, salt: string, amount: number): Point {
  const random = new SeededRandom(config.seed.seed).fork(salt);
  return { x: random.float(-amount, amount), y: random.float(-amount, amount) };
}

function seededIndex(config: WorldGeneratorConfig, salt: string, maxExclusive = 3): number {
  return new SeededRandom(config.seed.seed).fork(salt).int(0, Math.max(0, maxExclusive - 1));
}

function clampNumber(min: number, max: number, value: number): number {
  return Math.max(min, Math.min(max, value));
}

function path(...points: Point[]): Point[] {
  const out: Point[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const from = points[i];
    const to = points[i + 1];
    const dx = Math.sign(to.x - from.x);
    const dy = Math.sign(to.y - from.y);
    let x = from.x;
    let y = from.y;
    out.push({ x, y });
    while (x !== to.x) {
      x += dx;
      out.push({ x, y });
    }
    while (y !== to.y) {
      y += dy;
      out.push({ x, y });
    }
  }
  out.push(points[points.length - 1]);
  return uniquePoints(out);
}

function uniquePoints(points: readonly Point[]): Point[] {
  const seen = new Set<string>();
  return points.filter((point) => {
    const pointKey = key(point);
    if (seen.has(pointKey)) return false;
    seen.add(pointKey);
    return true;
  });
}

function key(point: Point): string {
  return `${point.x}:${point.y}`;
}
